import { Loader } from "@mantine/core";
import { ImageIcon } from "lucide-react";
import { useState } from "react";

import { ErrorAlert } from "../../platform/ErrorAlert";
import { GENERATIONS_PAGE, useJobList } from "../../platform/jobs/job-list";
import { outputKey, type OutputPick } from "../../platform/jobs/output-picks";
import { useStreamStatus } from "../../platform/stream-status";
import { LoadAhead, morePages } from "../../ui/LoadAhead";
import { SoundGeneration } from "./SoundGeneration";
import { SoundPreview, previewSounds } from "./SoundPreview";
import { SOUND_TYPES, isSoundJob } from "./sound-jobs";
import classes from "./sounds.module.css";

/** Live's empty My Generations: four placeholder tiles (capture 08, `SOUND_GENERATION_MAX_COUNT`). */
const PLACEHOLDERS = 4;

export interface SoundGenerationsProps {
  teamId: string;
  brandId: string;
}

/**
 * Live's My Generations on the Sounds page (`tabs/my-generations`): the brand's sound effects,
 * speech and music, newest first, one row each (G4's list, kept live by the stream), and live's
 * four placeholder tiles while there are none. One sound plays at a time, in the page's mini
 * player (live's single `SoundPreviewDialog`); a finished sound's ⤢ opens the lightbox over the
 * list's finished sounds (SA1 decision 8).
 */
export function SoundGenerations({ teamId, brandId }: SoundGenerationsProps) {
  // Live's three kinds alone, 5 at a time (issue 784).
  const jobs = useJobList(
    teamId,
    { status: "all", brandId, types: SOUND_TYPES, pageSize: GENERATIONS_PAGE },
    useStreamStatus(),
  );
  const [playing, setPlaying] = useState<OutputPick | null>(null);
  // The sound the lightbox shows (`outputKey`); null while it is closed.
  const [previewing, setPreviewing] = useState<string | null>(null);
  const listed = jobs.data?.pages.flatMap((page) => page.items) ?? [];
  const items = listed.filter(isSoundJob);
  const more = morePages(jobs);
  if (jobs.isPending) return <Loader size="sm" aria-label="Loading your generations" />;
  if (jobs.isError) return <ErrorAlert error={jobs.error} />;
  if (items.length === 0 && !jobs.hasNextPage) {
    return (
      <ul className={classes.placeholders} aria-label="My Generations">
        {Array.from({ length: PLACEHOLDERS }, (_, index) => (
          <li key={index} className={classes.placeholder}>
            <ImageIcon size={32} strokeWidth={1.5} aria-hidden />
          </li>
        ))}
      </ul>
    );
  }
  return (
    <section className={classes.generations} aria-label="My Generations">
      {items.map((job) => (
        <SoundGeneration
          key={job.id}
          teamId={teamId}
          brandId={brandId}
          job={job}
          playing={playing?.jobId === job.id ? playing.index : null}
          onPlay={(index) => setPlaying(index === null ? null : { jobId: job.id, index })}
          onPreview={(index) => {
            // The lightbox plays the sound itself: the mini player gives way.
            setPlaying(null);
            setPreviewing(outputKey({ jobId: job.id, index }));
          }}
          readAgain={() => void jobs.refetch()}
        />
      ))}
      {/* Live's: the next page as the end shows (`InfiniteScroll`), no button. */}
      <LoadAhead more={more} loaded={listed.length} label="Loading more generations" />
      <SoundPreview
        teamId={teamId}
        brandId={brandId}
        sounds={previewSounds(items)}
        current={previewing}
        onShow={setPreviewing}
        more={more}
        readAgain={() => void jobs.refetch()}
      />
    </section>
  );
}
